import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { SectionLabel, Headline, Field, Stamp } from "@/components/Doc";

const CopyLine = ({ label, url, testId }) => (
  <div data-testid={testId}>
    <span className="t-label text-olive">{label}</span>
    <div className="flex gap-3 items-end border-b border-rule pb-2 mt-1">
      <span className="t-mono text-sm break-all flex-1" data-testid={`${testId}-url`}>{url}</span>
      <button type="button" className="t-label text-carbon hover:text-stamp" onClick={() => { navigator.clipboard.writeText(url); toast("Copied"); }} data-testid={`${testId}-copy`}>Copy</button>
    </div>
  </div>
);

export default function Team() {
  const [f, setF] = useState({ sponsor_name: "", sponsor_email: "", company: "" });
  const [busy, setBusy] = useState(false);
  const [team, setTeam] = useState(null);
  const set = (e) => setF({ ...f, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/team", f);
      setTeam(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28" data-testid="team-page">
      <SectionLabel right="Form LH-91">Doc. 09 — The Team Audit</SectionLabel>
      <div className="grid lg:grid-cols-12 gap-12 mt-10">
        <div className="lg:col-span-5">
          <Headline as="h1" className="text-4xl sm:text-5xl lg:text-6xl">Run the audit across the room.</Headline>
          <p className="t-body text-lg sm:text-xl mt-6 leading-[1.65] max-w-[45ch]">One person's answers tell you what they believe. The spread between ten people's answers tells you what the company actually does. Open a team audit, send the invite link to everyone who will be in the room, and read the spread on one sheet.</p>
          <p className="t-body text-base mt-6 text-olive italic">Each respondent still receives their own findings. Respondents are told the sponsor will see their answers by name.</p>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          {team ? (
            <div className="settle bg-paper border border-carbon p-7 sm:p-10 relative shadow-[6px_6px_0_0_#D9C9A8] flex flex-col gap-7" data-testid="team-created">
              <div className="absolute -top-5 right-8"><Stamp color="red" rotate={-5} size="lg">Opened</Stamp></div>
              <div>
                <span className="t-label text-olive">Team audit — {team.company}</span>
                <h2 className="t-head text-3xl mt-2">Code <span data-testid="team-code">{team.code}</span></h2>
              </div>
              <CopyLine label="Invite link — send to the team" url={team.invite_url} testId="invite-link" />
              <CopyLine label="Spread sheet — sponsor only, keep private" url={team.sheet_url} testId="sheet-link" />
              <p className="t-body text-base text-olive">Both links have been sent to {team.sponsor_name}'s email. The sheet fills in as respondents submit.</p>
              <Link to={`/team/${team.code}?key=${team.sponsor_key}`} className="btn self-start" data-testid="open-sheet-btn">Open the Spread Sheet</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-paper border border-carbon p-7 sm:p-10 shadow-[6px_6px_0_0_#D9C9A8] flex flex-col gap-6" data-testid="team-form">
              <span className="t-label text-olive">Section 1 — Sponsor</span>
              <Field label="Your name" name="sponsor_name" value={f.sponsor_name} onChange={set} required testId="team-sponsor-name" />
              <Field label="Work email" name="sponsor_email" type="email" value={f.sponsor_email} onChange={set} required testId="team-sponsor-email" />
              <Field label="Company" name="company" value={f.company} onChange={set} required testId="team-company" />
              <button type="submit" className="btn btn-stamp self-start" disabled={busy} data-testid="team-submit-btn">{busy ? "Opening…" : "Open a Team Audit"}</button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
